<template>
  <div class="asale">
    <p class="asale__lead">
      공동구매몰은 기간을 정해 여는 특별할인입니다. 이름은 설명절, 추석명절, 어린이날처럼 적거나 날짜 문구로 적어도 됩니다.
    </p>

    <p v-if="errorMessage" class="asale__error">
      <AppIcon icon="mdi-alert-outline" :size="16" />
      {{ errorMessage }}
    </p>

    <section class="sui-card sui-card--flush" data-tour="admin-sales-list">
      <div class="sui-toolbar">
        <strong class="asale__title">특별할인 {{ sales.length }}개</strong>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="loading" @click="load">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
        <button type="button" class="sui-btn sui-btn--primary" data-tour="admin-sales-new" @click="openNew">
          <AppIcon icon="mdi-plus" :size="16" />
          특별할인 등록
        </button>
      </div>

      <div v-if="sales.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>이름</th>
              <th>시작</th>
              <th>종료</th>
              <th class="is-center">상태</th>
              <th class="is-center">관리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sale in sales" :key="sale.code">
              <td>
                <span class="sui-table__strong">{{ sale.name }}</span>
                <span class="sui-table__sub">{{ sale.code }}</span>
              </td>
              <td>{{ sale.start }}</td>
              <td>{{ sale.end }}</td>
              <td class="is-center">
                <span class="sui-badge" :class="phaseClass(sale)">{{ phaseLabel(sale) }}</span>
              </td>
              <td class="is-center">
                <div class="asale__actions">
                  <button type="button" class="sui-btn sui-btn--sm" @click="openEdit(sale)">수정</button>
                  <button type="button" class="sui-btn sui-btn--sm sui-btn--ghost" @click="onDelete(sale)">
                    삭제
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-calendar-blank-outline" :size="32" />
        <p>{{ loading ? '특별할인을 불러오는 중입니다…' : '등록된 공동구매 특별할인이 없습니다.' }}</p>
      </div>
    </section>

    <div v-if="editor" class="asale__modal" role="presentation" @keydown.esc.prevent="closeEditor">
      <div class="asale__backdrop" @click="closeEditor" />
      <div
        class="asale__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="asale-title"
        tabindex="-1"
      >
        <header class="asale__head">
          <h2 id="asale-title">{{ editor.code ? '특별할인 수정' : '특별할인 등록' }}</h2>
          <button type="button" class="asale__close" aria-label="특별할인 닫기" :disabled="saving" @click="closeEditor">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="asale__body">
          <label class="asale__field">
            <span>이름</span>
            <input
              v-model.trim="editor.name"
              type="text"
              maxlength="80"
              placeholder="추석명절, 설명절, 어린이날 또는 2026-09-01"
            />
          </label>
          <div class="asale__dates">
            <label class="asale__field">
              <span>시작일</span>
              <input v-model="editor.start" type="date" />
            </label>
            <label class="asale__field">
              <span>종료일</span>
              <input v-model="editor.end" type="date" />
            </label>
          </div>
          <p v-if="formError" class="asale__form-error">{{ formError }}</p>
        </div>

        <footer class="asale__foot">
          <button type="button" class="sui-btn sui-btn--ghost" :disabled="saving" @click="closeEditor">닫기</button>
          <button type="button" class="sui-btn sui-btn--primary" :disabled="saving" @click="onSave">
            {{ saving ? '저장 중…' : '저장' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  createAdminSale,
  deleteAdminSale,
  fetchAdminSales,
  updateAdminSale,
} from '@/api/admin'
import type { MallSale } from '@/api/mallProduct'
import { confirmDialog } from '@/composables/useConfirmDialog'

interface SaleDraft {
  code: string
  name: string
  start: string
  end: string
}

const sales = ref<MallSale[]>([])
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const formError = ref('')
const editor = ref<SaleDraft | null>(null)

function todayKey() {
  const today = new Date()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${today.getFullYear()}-${month}-${day}`
}

function phaseLabel(sale: MallSale) {
  const today = todayKey()
  if (today < sale.start) return '예정'
  if (today > sale.end) return '종료'
  return '진행'
}

function phaseClass(sale: MallSale) {
  const label = phaseLabel(sale)
  if (label === '진행') return 'sui-badge--green'
  if (label === '예정') return 'sui-badge--blue'
  return 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    sales.value = await fetchAdminSales()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '공동구매 특별할인을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function openNew() {
  formError.value = ''
  editor.value = { code: '', name: '', start: '', end: '' }
}

function openEdit(sale: MallSale) {
  formError.value = ''
  editor.value = { code: sale.code, name: sale.name, start: sale.start, end: sale.end }
}

function closeEditor() {
  if (saving.value) return
  editor.value = null
  formError.value = ''
}

async function onSave() {
  const draft = editor.value
  if (!draft || saving.value) return
  const name = draft.name.trim()
  if (!name) {
    formError.value = '특별할인 이름을 입력해 주세요.'
    return
  }
  if (!draft.start || !draft.end) {
    formError.value = '시작일과 종료일을 입력해 주세요.'
    return
  }
  if (draft.start > draft.end) {
    formError.value = '종료일은 시작일보다 빠를 수 없습니다.'
    return
  }
  saving.value = true
  formError.value = ''
  try {
    const body = { name, start: draft.start, end: draft.end }
    if (draft.code) await updateAdminSale(draft.code, body)
    else await createAdminSale(body)
    editor.value = null
    await load()
  } catch (error) {
    formError.value = error instanceof Error ? error.message : '특별할인을 저장하지 못했습니다.'
  } finally {
    saving.value = false
  }
}

async function onDelete(sale: MallSale) {
  const ok = await confirmDialog({
    title: '특별할인 삭제',
    message: `「${sale.name}」 특별할인을 삭제할까요?`,
    detail: '공동구매 상품은 이 특별할인에 묶여 있지 않습니다. 이미 나간 주문의 특별할인 이름은 그대로 남습니다.',
    confirmLabel: '삭제',
    tone: 'danger',
  })
  if (!ok) return
  errorMessage.value = ''
  try {
    await deleteAdminSale(sale.code)
    await load()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '특별할인을 삭제하지 못했습니다.'
  }
}

onMounted(() => {
  void load()
})
</script>

<style lang="scss" src="./AdminSalesPage.scss"></style>
